import React, { useState, useCallback, lazy, Suspense } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { useToast } from '@/components/ui/use-toast';
import { supabase } from '@/lib/customSupabaseClient';
import { format } from 'date-fns';
import BookingHeader from './Booking/BookingHeader';
import CriticalCSS from '@/components/CriticalCSS';
import { usePriceCalculator } from './Booking/usePriceCalculator';

// Lazy load non-critical components
const BookingForm = lazy(() => import('./Booking/BookingForm'));
const PriceEstimate = lazy(() => import('./Booking/PriceEstimate'));

const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

const Booking = () => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formState, setFormState] = useState({
    tripType: 'One Way',
    passengers: 1,
    vehicle: 'SUV',
    date: new Date(),
    time: '12:00',
    hours: 3,
    name: '',
    email: '',
    phone: '',
    pickup: '',
    dropoff: '',
    instructions: '',
    selectedPickup: null,
    selectedDropoff: null,
  });

  const { price, details, distance } = usePriceCalculator(formState);

  const handleFormChange = useCallback((field, value) => {
    setFormState(prevState => ({ ...prevState, [field]: value }));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const { name, email, phone, pickup, dropoff, selectedPickup, selectedDropoff, tripType, date, time } = formState;
    const isLocationRequired = tripType === 'One Way' || tripType === 'Wait & Return';

    if (!name || !email || !phone || (isLocationRequired && (!pickup || !dropoff || !selectedPickup || !selectedDropoff))) {
      toast({ variant: "destructive", title: "Missing Information", description: `Please fill all required fields. ${isLocationRequired ? 'Ensure you select valid pickup and drop-off locations from the suggestions.' : ''}` });
      setIsSubmitting(false);
      return;
    }

    const bookingDatetime = new Date(`${format(date, 'yyyy-MM-dd')}T${time}`);
    // Data for database insert (matches table schema)
    const dbBookingData = {
      customer_name: formState.name,
      customer_phone: formState.phone,
      customer_email: formState.email,
      pickup_address: formState.pickup || 'N/A',
      dropoff_address: formState.dropoff || 'N/A',
      pickup_time: bookingDatetime.toISOString(),
      vehicle_type: formState.vehicle || 'sedan',
      price: price > 0 ? parseFloat(price) : 0,
      distance_miles: distance ? parseFloat(distance) : null,
      estimated_duration_minutes: null,
      status: 'pending',
      special_requests: formState.instructions || '',
      notes: `Passengers: ${formState.passengers}`,
    };

    // Data for email function (matches email function expectations)
    const emailBookingData = {
      name: formState.name,
      email: formState.email,
      phone: formState.phone,
      pickup: formState.pickup || 'N/A',
      dropoff: formState.dropoff || 'N/A',
      datetime: bookingDatetime.toISOString(),
      vehicle: formState.vehicle || 'sedan',
      estimate_amount: price > 0 ? parseFloat(price) : 0,
      estimate_distance: distance ? parseFloat(distance) : null,
      trip_type: formState.tripType === 'Hourly' ? 'hourly' : formState.tripType === 'Round Trip' ? 'round_trip' : 'one_way',
      passengers: formState.passengers,
      hours: (formState.tripType === 'Hourly' || formState.tripType === 'As Directed') ? formState.hours : null,
      instructions: formState.instructions || '',
    };


    // Try to insert into database first
    let dbSuccess = false;
    const { data: insertData, error } = await supabase.from('bookings').insert([dbBookingData]).select();

    if (error) {
      console.error('Supabase booking error:', error);
    } else {
      dbSuccess = true;
    }

    // Always try to send emails regardless of database success
    try {
      await supabase.functions.invoke('send-booking-email', { body: { record: emailBookingData } });
    } catch (emailError) {
      console.error('Email sending error:', emailError);
      // Don't fail the booking if email fails
    }

    // Show success message regardless of database/email status
    if (dbSuccess) {
      toast({
        title: "Booking Request Received!",
        description: "We received your booking and sent you a confirmation email. We will contact you soon to finalize your ride.",
      });
    } else {
      toast({
        title: "Booking Request Received!",
        description: "We received your booking request and sent you a confirmation email. We will contact you soon to finalize your ride.",
      });
    }

    // Reset form
    setFormState({
      tripType: 'One Way', passengers: 1, vehicle: 'SUV',
      date: new Date(), time: '12:00', hours: 3,
      name: '', email: '', phone: '', pickup: '', dropoff: '', instructions: '',
      selectedPickup: null, selectedDropoff: null,
    });
    setIsSubmitting(false);
  };

  return (
    <>
      <Helmet>
        <title>Services & Booking - Akiblackcar</title>
        <meta name="description" content="Book your luxury ride with Akiblackcar. Choose from our premium fleet, select your trip type, and get an instant price estimate." />
      </Helmet>
      <CriticalCSS />
      <motion.div initial="hidden" animate="visible" variants={fadeIn} className="container mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 lg:py-16">
        <div className="booking-header">
          <BookingHeader />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
          <Suspense fallback={
            <div className="flex items-center justify-center p-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-gold"></div>
            </div>
          }>
            <BookingForm 
              formState={formState}
              onFormChange={handleFormChange}
              onSubmit={handleSubmit}
              isSubmitting={isSubmitting}
            />
          </Suspense>
          <Suspense fallback={
            <div className="flex items-center justify-center p-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-gold"></div>
            </div>
          }>
            <PriceEstimate 
              price={price}
              details={details}
              distance={distance}
              hours={formState.hours}
              tripType={formState.tripType}
            />
          </Suspense>
        </div>
      </motion.div>
    </>
  );
};

export default Booking;